import type { TFactTrajectoryPoint } from '../../service/model';

import type { TTrajectoryEditableField } from './editable-fields';

export type TrajectoryColumnField = Exclude<keyof TFactTrajectoryPoint, 'stationId' | 'projectedCrsKey'>;

export type TrajectoryColumnKind = 'number' | 'coordinate' | 'text' | 'select' | 'geo';

export type TrajectoryColumnEditor = 'number' | 'text' | 'select';

export type TrajectoryColumnDefinition = {
  field: TrajectoryColumnField;
  kind: TrajectoryColumnKind;
  editor?: TrajectoryColumnEditor;
  minWidth?: number;
};

/** Object key order is the default table column order. */
const COLUMN_KIND_BY_FIELD = {
  md: 'number',
  inc: 'number',
  azi: 'number',
  azMag: 'number',
  azTrue: 'number',
  aziGrid: 'number',
  tvd: 'number',
  tvdss: 'number',
  mdDelta: 'number',
  tvdDelta: 'number',
  ns: 'number',
  we: 'number',
  northing: 'coordinate',
  easting: 'coordinate',
  latitude: 'geo',
  longitude: 'geo',
  dls: 'number',
  tfo: 'number',
  rateTurn: 'number',
  rateBuild: 'number',
  mtf: 'number',
  gtf: 'number',
  closure: 'number',
  closureDirection: 'number',
  vsec: 'number',
  ahd: 'number',
  ddi: 'number',
  erd: 'number',
  tortuosity: 'number',
  upToPlan: 'number',
  rightToPlan: 'number',
  centrToCentr: 'number',
  typeTrajStation: 'select',
  comment: 'text',
} as const satisfies Record<TrajectoryColumnField, TrajectoryColumnKind>;

const COLUMN_EDITOR_BY_FIELD = {
  md: 'number',
  inc: 'number',
  azi: 'number',
  mtf: 'number',
  gtf: 'number',
  comment: 'text',
  typeTrajStation: 'select',
} as const satisfies Record<TTrajectoryEditableField, TrajectoryColumnEditor>;

const COLUMN_MIN_WIDTH: Partial<Record<TrajectoryColumnField, number>> = {
  northing: 100,
  easting: 100,
  latitude: 100,
  longitude: 100,
};

export const TRAJECTORY_COLUMN_DEFINITIONS: TrajectoryColumnDefinition[] = (
  Object.keys(COLUMN_KIND_BY_FIELD) as TrajectoryColumnField[]
).map((field) => {
  const kind = COLUMN_KIND_BY_FIELD[field];
  const editor = (COLUMN_EDITOR_BY_FIELD as Partial<Record<TrajectoryColumnField, TrajectoryColumnEditor>>)[field];

  return {
    field,
    kind,
    ...(editor ? { editor } : {}),
    ...(COLUMN_MIN_WIDTH[field] ? { minWidth: COLUMN_MIN_WIDTH[field] } : {}),
  };
});

const definitionByField = new Map(TRAJECTORY_COLUMN_DEFINITIONS.map((definition) => [definition.field, definition]));

export function getTrajectoryColumnDefinition(field: TrajectoryColumnField): TrajectoryColumnDefinition {
  const definition = definitionByField.get(field);

  if (!definition) {
    throw new Error(`Unknown trajectory column field: ${field}`);
  }

  return definition;
}
